import React from 'react';
import clsx from 'clsx';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {
	GENERIC_IMAGE_WATERMARK_CLASS_NAME,
	genericImageWatermarkClasses,
	GenericImageWatermarkClasses,
	getGenericImageWatermarkUtilityClass,
} from './genericImageWatermarkClasses';
import {useGenericImageWatermark} from './useGenericImageWatermark';

export type GenericImageWatermarkElement = HTMLDivElement;

export type GenericImageWatermarkRef =
	React.ForwardedRef<GenericImageWatermarkElement>;

export type GenericImageWatermarkOwnerState = WithClasses<
	GenericImageWatermarkClasses,
	{}
>;

const useUtilityClasses = (ownerState: GenericImageWatermarkOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getGenericImageWatermarkUtilityClass, classes);
};

const GenericImageWatermarkRoot = styled('div', {
	name: GENERIC_IMAGE_WATERMARK_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	...theme.mixins.watermark(),
	margin: 'auto',
}));

export interface GenericImageWatermarkProps
	extends Omit<
			React.DetailedHTMLProps<
				React.HTMLAttributes<GenericImageWatermarkElement>,
				GenericImageWatermarkElement
			>,
			'alt' | 'onLoad' | 'onResize'
		>,
		WithClasses<
			GenericImageWatermarkClasses,
			{
				queryParams?: Record<string, string | number>;
			}
		> {}

export const GenericImageWatermarkPrimitive = (
	{
		classes: _classes,
		className,
		queryParams,
		...props
	}: GenericImageWatermarkProps,
	ref: GenericImageWatermarkRef,
) => {
	const {watermarkSrc} = useGenericImageWatermark({queryParams});

	const ownerState: GenericImageWatermarkOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	if (!watermarkSrc) return null;

	return (
		<GenericImageWatermarkRoot
			ref={ref}
			{...props}
			className={clsx(classes.root, className)}
		/>
	);
};

export const GenericImageWatermark = React.forwardRef<
	GenericImageWatermarkElement,
	GenericImageWatermarkProps
>(GenericImageWatermarkPrimitive); // as (props: GenericImageWatermarkProps, ref: GenericImageWatermarkRef) => JSX.Element;

export default GenericImageWatermark;
